import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Pageable, httpByPageable, PagingResult } from '../../models/pageable';
import { UserInfoMember } from '../../models/user-info';
import {
    Container,
    ContainerCreateRequest,
    ContainerCreateResponse,
    ContainerJoinRequestList,
    ContainerMembership,
    ContainerPage,
    ContainerType,
    ContainerJoinRequestAnswer,
    ContainerJoinRequest,
} from '../../models/container.types';

@Injectable({ providedIn: 'root' })
export class ContainerService {
    private baseUrl: string;

    constructor(private http: HttpClient) {
        this.baseUrl = environment.backendUrl + '/rest/containers';
    }

    create(
        newContainer: ContainerCreateRequest
    ): Observable<ContainerCreateResponse> {
        return this.http.post<ContainerCreateResponse>(
            this.baseUrl,
            newContainer
        );
    }

    update(
        containerId: string,
        containerRequest: ContainerCreateRequest
    ): Observable<Container> {
        const url = `${this.baseUrl}/${containerId}`;
        return this.http.patch<Container>(url, containerRequest);
    }

    saveContainerImage(containerId: string, picture: File): Observable<string> {
        const url = `${this.baseUrl}/${containerId}/picture`;

        const formData = new FormData();
        formData.append('file', picture);
        return this.http
            .post<any>(url, formData, { observe: 'response' })
            .pipe(
                map((response: HttpResponse<any>) =>
                    response.headers.get('Location')
                )
            );
    }

    deleteContainerImage(containerId: string): Observable<void> {
        const url = `${this.baseUrl}/${containerId}/picture`;

        return this.http.delete<void>(url);
    }

    getById(containerId: string): Observable<Container> {
        const url = `${this.baseUrl}/${containerId}`;
        return this.http.get<Container>(url);
    }

    delete(containerId: string): Observable<unknown> {
        const url = `${this.baseUrl}/${containerId}`;
        return this.http.delete(url);
    }

    getAllUserContainers(
        types: ContainerType[],
        pageable: Pageable
    ): Observable<ContainerPage> {
        return this.fetchContainerFiltered(types, pageable, '/own');
    }

    getAllCommunityContainers(
        types: ContainerType[],
        pageable: Pageable
    ): Observable<ContainerPage> {
        return this.fetchContainerFiltered(types, pageable);
    }

    private fetchContainerFiltered(
        types: ContainerType[],
        pageable: Pageable,
        urlSuffix?: string
    ): Observable<ContainerPage> {
        let httpParams = httpByPageable(new HttpParams(), pageable);
        if (!!types && types.length > 0) {
            httpParams = httpParams.set('containerTypes', types.join(','));
        }

        const url = urlSuffix ? this.baseUrl + urlSuffix : this.baseUrl;

        return this.http.get<ContainerPage>(url, {
            params: httpParams,
        });
    }

    //------------Membership
    getOwner(containerId: string): Observable<UserInfoMember> {
        const url = `${this.baseUrl}/${containerId}/memberships/owner`;
        return this.http.get<UserInfoMember>(url);
    }

    getMyMembership(containerId: string): Observable<ContainerMembership> {
        const url = `${this.baseUrl}/${containerId}/memberships/single`;
        return this.http.get<ContainerMembership>(url);
    }

    getContainerMembersWithoutOwner(containerId: string) {
        const url = `${this.baseUrl}/${containerId}/memberships`;

        return this.http.get<PagingResult<ContainerMembership>>(url, {
            params: {
                pageSize: -1,
            },
        });
    }

    deleteMembership(containerId: string, memberId: string) {
        const url = `${this.baseUrl}/${containerId}/memberships/${memberId}`;
        return this.http.delete(url);
    }

    //------------JoinRequest
    createJoinRequest(containerId: string, termsAccepted: boolean) {
        const url = `${this.baseUrl}/${containerId}/requests`;
        return this.http.post<ContainerJoinRequest>(url, {
            termsAccepted: termsAccepted,
        });
    }

    answerJoinRequest(
        joinContainer: ContainerJoinRequestAnswer
    ): Observable<void> {
        const url = `${this.baseUrl}/${joinContainer.containerId}/requests/answer`;
        return this.http.post<any>(url, joinContainer);
    }

    getOpenJoinRequestsForContainer(
        containerId: string
    ): Observable<ContainerJoinRequestList> {
        const url = `${this.baseUrl}/${containerId}/requests`;
        return this.http.get<ContainerJoinRequestList>(url, {
            params: {
                asc: false,
            },
        });
    }
}
